import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { CheckCircle2, KeyRound, UserX } from 'lucide-react';
import { StatusBadge } from '@/components/ui/status-badge';

export interface LoginStatusBadgeProps {
  /** The employee's or contact's Clerk user id — present means a login exists. */
  clerkUserId?: string | null;
  /** Persisted auth role of an employee login ('admin' | 'employee'). Contacts pass none. */
  authRole?: string | null;
  /**
   * Detail-only "Signed in" flag. The list badge never sets this (it would
   * require a per-row Clerk call / N+1); only the detail view reports it.
   */
  signedIn?: boolean;
  className?: string;
}

/**
 * Read-only badge summarising an employee or contact login status:
 *   - No login (no clerk_user_id)
 *   - Login created (+ role)
 *   - Signed in (detail-only, + role)
 *
 * Derived purely from returned employee or contact fields — no per-row Clerk
 * call. A contact (parent) login has no role, so no role suffix is shown.
 */
export function LoginStatusBadge({
  clerkUserId,
  authRole,
  signedIn,
  className,
}: LoginStatusBadgeProps) {
  const { i18n } = useLingui();

  const hasLogin = !!clerkUserId;

  const roleLabel =
    authRole === 'admin'
      ? i18n._(msg({ id: 'employees.login.role.admin', message: 'Admin' }))
      : authRole === 'employee'
        ? i18n._(msg({ id: 'employees.login.role.staff', message: 'Staff' }))
        : '';

  if (!hasLogin) {
    return (
      <StatusBadge
        tone="neutral"
        icon={UserX}
        label={i18n._(msg({ id: 'employees.login.status.noLogin', message: 'No login' }))}
        className={className}
      />
    );
  }

  const roleSuffix = roleLabel ? ` · ${roleLabel}` : '';

  if (signedIn) {
    return (
      <StatusBadge
        tone="success"
        icon={CheckCircle2}
        label={`${i18n._(msg({ id: 'employees.login.status.signedIn', message: 'Signed in' }))}${roleSuffix}`}
        className={className}
      />
    );
  }

  return (
    <StatusBadge
      tone="info"
      icon={KeyRound}
      label={`${i18n._(msg({ id: 'employees.login.status.created', message: 'Login created' }))}${roleSuffix}`}
      className={className}
    />
  );
}
